import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  showDetails: boolean;
}

/**
 * Polished error boundary — no raw React crash page.
 * Stack traces only in development.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, showDetails: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error('ErrorBoundary caught', error, info.componentStack);
    }
  }

  private reload = (): void => {
    window.location.reload();
  };

  private home = (): void => {
    window.location.assign('/');
  };

  override render(): ReactNode {
    const { error, showDetails } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="error-boundary" role="alert">
        <div className="error-boundary-card">
          <h1>Something went wrong.</h1>
          <p className="muted">
            The page hit an unexpected error. Your catalogue data on this device
            should still be safe — try reloading.
          </p>
          <div className="row" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
            <button type="button" className="btn btn-primary" onClick={this.reload}>
              Reload
            </button>
            <button type="button" className="btn" onClick={this.home}>
              Return Home
            </button>
            {import.meta.env.DEV && (
              <button
                type="button"
                className="btn"
                onClick={() =>
                  this.setState((s) => ({ showDetails: !s.showDetails }))
                }
              >
                Developer Details
              </button>
            )}
          </div>
          {import.meta.env.DEV && showDetails && (
            <pre className="error-boundary-details">
              {error.name}: {error.message}
              {'\n'}
              {error.stack}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
